import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserDeviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-device/user-device-api';
import { RegisterDeviceRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/user/register-device.request';
import { DevicePlatform } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/device-platform.enum';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { WebViewBridgeService } from './webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';

interface PendingToken {
    token: string;
    platform: DevicePlatform;
}

@Injectable({
    providedIn: 'root'
})
export class DeviceTokenService implements OnDestroy {
    private userDeviceApi: UserDeviceApi;
    private authApi: AuthApi;
    private currentToken: string | null = null;
    private pendingToken: PendingToken | null = null;
    private isRegisteredSubject = new BehaviorSubject<boolean>(false);
    public isRegistered$: Observable<boolean> = this.isRegisteredSubject.asObservable();

    private onFcmTokenCallback: ((token: string, platform: string) => void) | null = null;
    private onFcmTokenRefreshCallback: ((token: string, platform: string) => void) | null = null;
    private onNotificationTapCallback: ((data: any) => void) | null = null;

    constructor(private webViewBridgeService: WebViewBridgeService) {
        this.userDeviceApi = new UserDeviceApi();
        this.authApi = new AuthApi();
        this.initializeListeners();
    }

    private initializeListeners(): void {
        // Called by Flutter when FCM token is available
        this.onFcmTokenCallback = (token: string, platform: string) => {
            console.log('FCM token received from mobile:', token.substring(0, 20) + '...');
            this.handleTokenReceived(token, platform as DevicePlatform);
        };
        (window as any).onFcmToken = this.onFcmTokenCallback;

        // Called by Flutter when FCM token is refreshed
        this.onFcmTokenRefreshCallback = (token: string, platform: string) => {
            console.log('FCM token refreshed:', token.substring(0, 20) + '...');
            this.handleTokenReceived(token, platform as DevicePlatform);
        };
        (window as any).onFcmTokenRefresh = this.onFcmTokenRefreshCallback;

        // Called by Flutter when notification is tapped
        this.onNotificationTapCallback = (data: any) => {
            console.log('Notification tapped:', data);
            if (data && data.deepLink) {
                window.location.href = data.deepLink;
            }
        };
        (window as any).onNotificationTap = this.onNotificationTapCallback;
    }

    private async handleTokenReceived(token: string, platform: DevicePlatform): Promise<void> {
        const isAuthenticated = await this.authApi.isAuthenticate();

        if (isAuthenticated) {
            await this.registerToken(token, platform);
        } else {
            // Store for later registration after login
            this.pendingToken = { token, platform };
            console.log('Token stored for post-login registration');
        }
    }

    async registerToken(token: string, platform: DevicePlatform): Promise<void> {
        try {
            const request: RegisterDeviceRequest = {
                fcmToken: token,
                platform,
                deviceName: this.getDeviceName(platform),
            };

            await this.userDeviceApi.register(request);
            this.currentToken = token;
            this.isRegisteredSubject.next(true);
            console.log('Device registered successfully');
        } catch (error) {
            console.error('Failed to register device:', error);
        }
    }

    async registerPendingToken(): Promise<void> {
        if (this.pendingToken) {
            await this.registerToken(this.pendingToken.token, this.pendingToken.platform);
            this.pendingToken = null;
        }
    }

    async unregisterCurrentDevice(): Promise<void> {
        if (this.currentToken) {
            try {
                await this.userDeviceApi.unregister({ fcmToken: this.currentToken });
                this.currentToken = null;
                this.isRegisteredSubject.next(false);
                console.log('Device unregistered successfully');
            } catch (error) {
                console.error('Failed to unregister device:', error);
            }
        }
    }

    requestTokenFromMobile(): void {
        // Request FCM token from Flutter via JavaScript channel
        this.webViewBridgeService.postMessageToChannel(WebViewAction.fcmToken, 'request');
    }

    private getDeviceName(platform: DevicePlatform): string {
        if (platform === DevicePlatform.IOS) {
            return 'iPhone';
        } else if (platform === DevicePlatform.ANDROID) {
            return 'Android Device';
        }
        return 'Web Browser';
    }

    get hasPendingToken(): boolean {
        return this.pendingToken !== null;
    }

    ngOnDestroy(): void {
        // Clean up the global functions
        if (this.onFcmTokenCallback) {
            delete (window as any).onFcmToken;
            this.onFcmTokenCallback = null;
        }
        if (this.onFcmTokenRefreshCallback) {
            delete (window as any).onFcmTokenRefresh;
            this.onFcmTokenRefreshCallback = null;
        }
        if (this.onNotificationTapCallback) {
            delete (window as any).onNotificationTap;
            this.onNotificationTapCallback = null;
        }
    }
}
